// Al entrar una cuenta, el "buzón" de push de este navegador pasa a su nombre (ver
// `sincronizarPush` en lib/push). Se monta UNA sola vez, en App.tsx.
//
// Sin esto, quien usaba dos cuentas en el mismo navegador veía "Notificaciones activadas" con la
// segunda, pero el backend tenía el dispositivo a nombre de la primera y los avisos de la segunda
// no llegaban nunca.

import { useEffect } from 'react';
import { debeSincronizarPush, esperaParaReintentarPush, sincronizarPush } from '../lib/push';
import { supabase } from '../lib/supabase';

export function useSincronizarPush(): void {
  useEffect(() => {
    let sincronizado: string | null = null;
    let vivo = true;
    let reintento: ReturnType<typeof setTimeout> | null = null;

    const frenarReintento = () => {
      if (reintento) {
        clearTimeout(reintento);
        reintento = null;
      }
    };

    // Si el registro falla (sin red, el backend caído), se reintenta solo mientras siga la MISMA
    // cuenta. Sin esto, un corte justo al entrar dejaba el buzón a nombre de la cuenta anterior
    // hasta recargar (review de Greptile en #157).
    const intentar = (usuario: string, intento: number) => {
      void sincronizarPush().then((r) => {
        const espera = esperaParaReintentarPush(r, intento, sincronizado === usuario);
        if (espera === null || !vivo) return;
        reintento = setTimeout(() => intentar(usuario, intento + 1), espera);
      });
    };

    const { data: auth } = supabase.auth.onAuthStateChange((evento, sesion) => {
      const usuario = sesion?.user?.id ?? null;
      if (!usuario) {
        // Salió: si vuelve a entrar la misma cuenta hay que registrar de nuevo, porque en el medio
        // pudo haber entrado otra en este navegador.
        sincronizado = null;
        frenarReintento();
        return;
      }
      if (!debeSincronizarPush(evento, sincronizado, usuario)) return;
      sincronizado = usuario;
      frenarReintento();
      // Fuera del callback: `sincronizarPush` le pide la sesión a supabase-js, y hacerlo adentro de
      // su propio aviso de sesión puede trabarlo (mismo cuidado que en usePresencia).
      setTimeout(() => intentar(usuario, 0), 0);
    });

    return () => {
      vivo = false;
      auth.subscription.unsubscribe();
      frenarReintento();
    };
  }, []);
}
